import React, { useMemo } from 'react';
import { IMessageMetadata } from '../../types/chat';
import { useChatStore } from '../../hooks/useChatStore';
import { useAppStore } from '../../hooks/useAppStore';
import { resolveCitationHref } from '../../utils/backendUrl';

// Filter out empty text nodes, whitespace-only elements, and empty React elements
const filterEmptyChildren = (children: React.ReactNode) => {
  return React.Children.toArray(children).filter(child => {
    if (typeof child === 'string') {
      return child.trim() !== '';
    }
    if (React.isValidElement(child)) {
      // Check if it's an empty element or has no meaningful content
      const props = child.props as any;
      if (props?.children) {
        const childChildren = React.Children.toArray(props.children);
        return childChildren.some(c => {
          if (typeof c === 'string') {
            return c.trim() !== '';
          }
          return true;
        });
      }
      return true;
    }
    return child !== null && child !== undefined;
  });
};

// Paragraph component
export const MarkdownParagraph = ({ children, ...props }: any) => (
  <p className="mb-1 last:mb-0 text-hex-text-dark font-manrope text-md font-medium leading-[140%]" {...props}>
    {children}
  </p>
);

// Heading components
export const MarkdownH1 = ({ children, ...props }: any) => (
  <h1 className="text-xl font-bold mb-1 mt-2 first:mt-0 text-chat-dark" {...props}>
    {children}
  </h1>
);

export const MarkdownH2 = ({ children, ...props }: any) => (
  <h2 className="text-lg font-bold mb-1 mt-2 first:mt-0 text-chat-dark" {...props}>
    {children}
  </h2>
);

export const MarkdownH3 = ({ children, ...props }: any) => (
  <h3 className="text-md font-bold mb-1 mt-2 first:mt-0 text-chat-dark" {...props}>
    {children}
  </h3>
);

export const MarkdownH4 = ({ children, ...props }: any) => (
  <h4 className="text-md font-bold mb-1 mt-2 first:mt-0 text-chat-dark" {...props}>
    {children}
  </h4>
);

export const MarkdownH5 = ({ children, ...props }: any) => (
  <h5 className="text-sm font-semibold mb-1 mt-2 first:mt-0 text-chat-dark" {...props}>
    {children}
  </h5>
);

export const MarkdownH6 = ({ children, ...props }: any) => (
  <h6 className="text-xs font-semibold mb-1 mt-2 first:mt-0 text-chat-dark" {...props}>
    {children}
  </h6>
);

// List components
export const MarkdownUl = ({ children, ...props }: any) => {
  const filteredChildren = filterEmptyChildren(children);
  return (
    <ul className="list-disc list-inside mb-1 ml-2 text-hex-text-dark space-y-0" {...props}>
      {filteredChildren}
    </ul>
  );
};

export const MarkdownOl = ({ children, ...props }: any) => {
  const filteredChildren = filterEmptyChildren(children);
  return (
    <ol className="list-decimal list-outside mb-1 ml-6 text-hex-text-dark space-y-0" {...props}>
      {filteredChildren}
    </ol>
  );
};

export const MarkdownLi = ({ children, ...props }: any) => {
  const filteredChildren = filterEmptyChildren(children);
  return (
    <li
      className="text-hex-text-dark font-manrope text-md font-medium leading-[140%] [&>p]:inline [&>ul]:block [&>ul]:mt-1 [&>ul]:ml-2"
      {...props}
    >
      {filteredChildren}
    </li>
  );
};

// Code components
export const MarkdownCode = ({ children, className, ...props }: any) => {
  const isInline = !className;
  return isInline ? (
    <code className="bg-gray-100 px-1 py-0.5 rounded text-md font-mono break-all max-w-full" {...props}>
      {children}
    </code>
  ) : (
    <code className={`${className} block break-all max-w-full min-w-0`} {...props}>{children}</code>
  );
};

export const MarkdownPre = ({ children, ...props }: any) => (
  <div className="w-full max-w-full min-w-0">
    <pre className="bg-gray-100 p-2 rounded text-md font-mono mb-1 max-w-full break-all whitespace-pre-wrap min-w-0" style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }} {...props}>
      {children}
    </pre>
  </div>
);

// Text formatting components
export const MarkdownBlockquote = ({ children, ...props }: any) => (
  <blockquote className="border-l-4 border-gray-300 pl-3 italic mb-1 bg-gray-50 py-1 text-chat-dark" {...props}>
    {children}
  </blockquote>
);

export const MarkdownStrong = ({ children, ...props }: any) => (
  <strong className="font-bold text-chat-dark" {...props}>
    {children}
  </strong>
);

export const MarkdownEm = ({ children, ...props }: any) => (
  <em className="italic text-chat-dark" {...props}>
    {children}
  </em>
);

export const MarkdownLink = ({ href, children, ...props }: any) => (
  <a 
    href={href} 
    className="text-blue-600 hover:text-blue-800 underline" 
    target="_blank" 
    rel="noopener noreferrer"
    {...props}
  >
    {children}
  </a>
);

// Table components
export const MarkdownTable = ({ children, ...props }: any) => (
  <div className="overflow-x-auto mb-1">
    <table className="min-w-full border-collapse border border-gray-300 text-sm" {...props}>
      {children}
    </table>
  </div>
);

export const MarkdownThead = ({ children, ...props }: any) => (
  <thead className="bg-gray-100" {...props}>
    {children}
  </thead>
);

export const MarkdownTbody = ({ children, ...props }: any) => (
  <tbody {...props}>
    {children}
  </tbody>
);

export const MarkdownTr = ({ children, ...props }: any) => (
  <tr className="border-b border-gray-200" {...props}>
    {children}
  </tr>
);

export const MarkdownTh = ({ children, ...props }: any) => (
  <th className="border border-gray-300 px-2 py-1 text-left font-bold" {...props}>
    {children}
  </th>
);

export const MarkdownTd = ({ children, ...props }: any) => (
  <td className="border border-gray-300 px-2 py-1" {...props}>
    {children}
  </td>
);

// Other components
export const MarkdownHr = (props: any) => (
  <hr
    className="border-0 border-t border-gray-300 py-0 my-[6px]"
    {...props}
  />
);

export const MarkdownDel = ({ children, ...props }: any) => (
  <del className="line-through text-gray-500" {...props}>
    {children}
  </del>
);

export const MarkdownInput = ({ type, checked, ...props }: any) => (
  <input
    type={type}
    checked={checked}
    disabled
    className="mr-2"
    {...props}
  />
);

export const MarkdownRefLink = ({ id, children, metadata, ...props }: any) => {
  const chatStore = useChatStore();
  const { setContractToPreview } = useAppStore();
  const currentMetadata = metadata as IMessageMetadata | undefined;
  const dataPage = props?.["data-page"]

  // When a data-page is present, the ref points at an uploaded file rather than a policy citation.
  const refFile = useMemo(() => {
    if(!dataPage || !currentMetadata?.files){
      return null
    }

    return currentMetadata.files.find(file => String(file?.id) === String(id)) ?? null
  }, [currentMetadata, id, dataPage])

  const currentPolicy = useMemo(() => {
    if (refFile || !currentMetadata) {
      return null;
    }

    const currentPolicy = currentMetadata?.policies?.find(policy=> String(policy?.id) === String(id))

    return {
      ...currentPolicy,
      document_id: currentPolicy?.document_id || currentPolicy?.['id'],
      // A tenant contract's source_url is a root-relative API path (REV-2434).
      source_url: resolveCitationHref(currentPolicy?.source_url)
    }
  }, [currentMetadata, id, refFile])

  // Link to pdf viewer (with page to scroll to)
  if(refFile){
    const targetPage = Number(dataPage)

    return (
      <a
        href="#"
        role="button"
        onClick={(e) => {
          e.preventDefault();
          chatStore.setFileToPreview({
            ...refFile,
            ...Number.isFinite(targetPage) ? {page: targetPage} : {}
          });
        }}
        className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
        {...props}
      >
        {children}
      </a>
    )
  }

  // If no link is found, don't show empty link
  if(!currentPolicy?.source_url){
    return <>{children}</>
  }

  // s3:// URIs aren't browser-navigable, so route those through the panel viewer
  // (which fetches the content by document id) instead of opening a dead tab.
  if (currentPolicy.source_url.startsWith("s3://")) {
    // llm-server policy metadata is { id, name, source_url } -- id is the ES hash,
    // not a document id the panel viewer can fetch by. Without a real document_id
    // there's nothing to preview, so render plain text instead of a link that 404s.
    if (!currentPolicy.document_id) {
      return <>{children}</>;
    }

    return (
      <a
        href="#"
        role="button"
        onClick={(e) => {
          e.preventDefault();
          const preview = {
            document_id: String(currentPolicy.document_id),
            title: currentPolicy.name ?? String(children),
          };

          setContractToPreview(preview);
        }}
        className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
        {...props}
      >
        {children}
      </a>
    );
  }

  return (
    <a href={currentPolicy.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline" {...props}>
      {children}
    </a>
  )
};

// Export all components as a single object for easy import
export const markdownComponents = {
  p: MarkdownParagraph,
  h1: MarkdownH1,
  h2: MarkdownH2,
  h3: MarkdownH3,
  h4: MarkdownH4,
  h5: MarkdownH5,
  h6: MarkdownH6,
  ul: MarkdownUl,
  ol: MarkdownOl,
  li: MarkdownLi,
  code: MarkdownCode,
  pre: MarkdownPre,
  blockquote: MarkdownBlockquote,
  strong: MarkdownStrong,
  em: MarkdownEm,
  a: MarkdownLink,
  table: MarkdownTable,
  thead: MarkdownThead,
  tbody: MarkdownTbody,
  tr: MarkdownTr,
  th: MarkdownTh,
  td: MarkdownTd,
  hr: MarkdownHr,
  del: MarkdownDel,
  input: MarkdownInput,
  ref: MarkdownRefLink,
};
